'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The PFAS Left Microwave Popcorn Bags. The Plastic Film Did Not."
      author="MicroPlastics Research Desk"
      date="August 21, 2026"
      readTime="11 min read"
      heroImage="/blog/heroes/home.webp"
      heroImageAlt="A microwave popcorn bag contains a metallized PET susceptor film that reaches around 450°F against the food, separate from the grease-proofing PFAS that US manufacturers stopped selling in 2024"
    >
      <BlogLastUpdated iso="2026-08-21" formatted="August 21, 2026" />

      <BlogTLDR>
        <strong>The PFAS warning you have read about microwave popcorn is
        largely out of date for American bags — and the plastic nobody
        mentions is still in there.</strong>{' '}
        On <strong>28 February 2024</strong> the FDA announced that
        grease-proofing substances containing PFAS are no longer sold by
        manufacturers for food-contact use in the US, naming microwave popcorn
        bags directly. Read that precisely:{' '}
        <strong>it was a voluntary phase-out of sales, not a ban and not a
        recall.</strong> The study everyone still quotes — daily popcorn eaters
        carrying up to <strong>63% higher</strong> serum PFAS — is built on
        NHANES data from <strong>2003 to 2014</strong>, entirely before that
        phase-out. It is not wrong; it is historical. But the bag was never
        only its coating.{' '}
        <strong>Every microwave popcorn bag contains a susceptor: a thin,
        metal-coated PET film whose job is to get extremely hot right against
        your food.</strong> Pop Secret&rsquo;s own FAQ says the bag reaches
        around <strong>450&nbsp;°F</strong>. PET melts at 255&ndash;265&nbsp;°C
        and begins degrading near its melting point; 450&nbsp;°F is
        232&nbsp;°C. The film is engineered to run roughly 25&ndash;30&nbsp;°C
        under the melting point of the plastic it is made from. Nobody has
        measured particle release from one, and the honest fix is to stop using
        the bag rather than to worry about it.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>The US PFAS situation genuinely changed in 2024.</strong> FDA announced on 28 February 2024 that grease-proofing PFAS are no longer being sold for food-contact use, microwave popcorn bags named explicitly.</>,
          <><strong>But it was voluntary, and it covered sales.</strong> Manufacturers committed in 2020 and exited the market; no product was banned, recalled, or tested on the way out.</>,
          <><strong>The famous 63% figure is pre-2014 data.</strong> Susmann et al. drew on NHANES serum and diet records from 2003&ndash;2014 — the era when the coatings were still standard. Quoting it as current is the common error.</>,
          <><strong>Bags elsewhere still leach.</strong> A 2025 Taiwanese study found seven PFAS migrating from five bags at 5.2 to 274 ng/g. Those were not US bags and the paper does not say when they were made.</>,
          <><strong>Two regulators, same samples, wildly different verdicts.</strong> Under the EFSA method those bags scored 0.01&ndash;0.8 (minimal risk). Under the EPA method, 0.3&ndash;142.3, with four of five over the threshold.</>,
          <><strong>The susceptor is the overlooked part.</strong> A metallized PET film laminated into the bag, designed to absorb microwaves and heat the kernels from below. It sits directly against the food.</>,
          <><strong>450&nbsp;°F is the brand&rsquo;s own number.</strong> Pop Secret states the bag reaches about that when popping. PET melts at 255&ndash;265&nbsp;°C — 450&nbsp;°F is 232&nbsp;°C, a margin of roughly 25&ndash;30&nbsp;°C.</>,
          <><strong>No one has counted particles from a popcorn bag.</strong> Any number you see for that is invented. The case for switching is the heat and the film, not a measurement.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and where each one comes from"
        stats={[
          { value: '28 Feb 2024', label: 'FDA announced grease-proofing PFAS off the US market', context: 'a voluntary phase-out of sales following 2020 manufacturer commitments; microwave popcorn bags named explicitly', source: 'US FDA', sourceUrl: 'https://www.fda.gov/food/process-contaminants-food/market-phase-out-grease-proofing-substances-containing-pfas' },
          { value: '~450 °F', label: 'temperature the bag reaches when popping', context: 'the manufacturer’s own figure — 232 °C, against a PET melting point of 255–265 °C', source: 'Pop Secret FAQ (manufacturer)', sourceUrl: 'https://www.popsecret.com/faq' },
          { value: '63%', label: 'higher serum PFAS in daily microwave popcorn eaters', context: 'from NHANES data covering 2003–2014, entirely before the phase-out', source: 'Susmann et al., Environmental Health Perspectives, 2019', sourceUrl: 'https://ehp.niehs.nih.gov/doi/10.1289/EHP4092' },
          { value: '0.8 vs 142.3', label: 'top hazard ratio for the same bags under EFSA vs EPA methods', context: 'five Taiwanese bags, seven PFAS detected; the frameworks disagree by orders of magnitude on identical data', source: 'Hsu et al., Molecules, 2025', sourceUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12073814/' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        If you avoided microwave popcorn because of &ldquo;forever
        chemicals&rdquo;, the specific thing you were avoiding has largely been
        removed from American bags, and most of the internet has not caught up.
        That is worth knowing, and it is a real win for a regulatory process
        that rarely produces one.
      </p>
      <p>
        It is also not the whole bag. A microwave popcorn bag is a laminate:
        paper on the outside, and bonded inside it a strip of plastic film with
        a microscopically thin layer of metal on it. That strip is the
        susceptor, and it exists because microwaves alone will not brown or
        sear anything. The susceptor converts microwave energy into direct
        heat, which is what gets the kernels to popping temperature. It is made
        of polyester — PET, the same polymer as a drinks bottle — and it is
        designed to get hotter than almost anything else in your kitchen while
        touching your food.
      </p>

      <h2>What actually changed in 2024</h2>
      <p>
        The FDA&rsquo;s announcement on <strong>28 February 2024</strong> is the
        pivot point, and the wording matters more than the headline did. The
        agency said grease-proofing substances containing PFAS are{' '}
        <em>no longer being sold by manufacturers for food-contact use in the
        US market</em>. The named applications were fast-food wrappers,
        microwave popcorn bags, takeaway paperboard and pet food bags.
      </p>
      <p>
        Three qualifications, all of which get flattened in most coverage:
      </p>
      <ul>
        <li>
          <strong>It was voluntary.</strong> The FDA obtained commitments from
          manufacturers in 2020 after a post-market safety assessment, and the
          companies exited the market — most of them ahead of the agreed date.
          That is a negotiated withdrawal, not a prohibition.
        </li>
        <li>
          <strong>It covered sales of the substances, not use of the
          packaging.</strong> Nothing was recalled. Packaging already
          manufactured was not withdrawn, and there was no testing programme
          attached to confirm what left the supply chain or when.
        </li>
        <li>
          <strong>It is a US measure.</strong> It says nothing about bags made
          for or sold in other markets.
        </li>
      </ul>
      <p>
        So the accurate sentence is: the grease-proofing PFAS that defined this
        product&rsquo;s reputation should be gone from bags manufactured for
        the US market since roughly 2024, on the manufacturers&rsquo; word,
        with no independent verification programme behind it. That is
        considerably better than where things stood in 2019. It is not the same
        as proven absence.
      </p>

      <h2>Why the famous statistic is now a historical one</h2>
      <p>
        Nearly every article on this subject cites the same finding: people who
        eat microwave popcorn have markedly higher PFAS in their blood, with
        daily eaters running up to <strong>63% above average</strong>. It comes
        from Susmann and colleagues at the Silent Spring Institute, published in{' '}
        <em>Environmental Health Perspectives</em> in 2019, and it is a good
        study — a nationally representative sample, serum measurements paired
        with dietary recall, several PFAS compounds moving together.
      </p>
      <p>
        The detail that gets dropped is the window. The analysis used NHANES
        data from <strong>2003 to 2014</strong>. Every blood sample in it was
        drawn while the grease-proofing coatings were still in normal
        commercial use. The study is strong evidence that those coatings
        reached people. It is not evidence about a bag bought this year, and
        presenting it as though it were is the single most common mistake in
        the coverage.
      </p>
      <p>
        Nobody has repeated it post-phase-out. That is the study worth wanting,
        and until it exists the honest position is that the exposure route was
        real and the coating is reported gone.
      </p>

      <h2>What a 2025 test of actual bags found</h2>
      <p>
        The most recent laboratory work is Hsu and colleagues at National Cheng
        Kung University, published in <em>Molecules</em> in 2025. They bought
        five microwave popcorn bags from online stores{' '}
        <strong>in Taiwan</strong> and measured what migrated out of them into
        food simulants — 10% and 50% ethanol, held at 70&nbsp;°C for two hours.
      </p>
      <p>
        They identified <strong>seven PFAS</strong> — PFHxA, PFHpA, PFOA, PFNA,
        PFDA, PFUnA and PFDoA — migrating at concentrations from{' '}
        <strong>5.2&nbsp;ng/g up to 274&nbsp;ng/g</strong>, the highest being
        PFOA from one sample into the 50% ethanol simulant.
      </p>
      <p>
        Two things must be said about that before anyone panics. These were
        Taiwanese bags, not American ones, and{' '}
        <strong>the paper does not state whether they predate any phase-out</strong>.
        It is not evidence that US bags still leach PFAS. What it does show is
        that the problem was never globally solved by a US market agreement.
      </p>

      <h3>The finding underneath the finding</h3>
      <p>
        The most interesting result in that paper is not the concentrations. It
        is what happened when the authors ran the same measurements through two
        different regulatory risk frameworks.
      </p>
      <p>
        Using the <strong>EFSA</strong> approach, which considers four PFAS,
        hazard ratios came out between <strong>0.01 and 0.8</strong> — every
        sample below the threshold, which the authors describe as suggesting
        minimal health risks. Using the <strong>EPA</strong> approach, which
        converts all detected PFAS into PFOA equivalents, the same five bags
        scored <strong>0.3 to 142.3</strong>, with{' '}
        <strong>four of the five exceeding 1</strong> and therefore indicating
        potential health concerns.
      </p>
      <p>
        Same bags, same laboratory, same numbers going in — and the two major
        regulatory methods disagree by more than two orders of magnitude.
        That is not a flaw in the study; it is an honest picture of how
        unsettled PFAS risk assessment still is, and it is worth holding onto
        the next time you read a confident claim in either direction.
      </p>

      <h2>The part nobody covers: the susceptor</h2>
      <p>
        Strip the PFAS question out entirely and there is still a piece of
        plastic in the bag, by design, doing the hottest job in your kitchen.
      </p>
      <p>
        A susceptor is a very thin layer of aluminium vapour-deposited onto a
        polyester film, laminated to the paper. Microwaves pass through paper
        and food but are absorbed by that metal layer, which converts the
        energy to heat. Without it, the kernels at the bottom of the bag would
        not reach popping temperature before the ones above them burned. Every
        microwave popcorn bag has one; so do the crisping sleeves on frozen
        sandwiches and pizzas.
      </p>
      <p>
        The temperatures involved are the point.{' '}
        <strong>Pop Secret&rsquo;s own FAQ states that the bag reaches around
        450&nbsp;°F when the moisture in the kernel turns to steam.</strong>{' '}
        That is <strong>232&nbsp;°C</strong>. Patent literature for susceptor
        packaging describes films reaching up to 500&nbsp;°F (about
        260&nbsp;°C), with designs specifically engineered so the polyester
        adjacent to the susceptor does not exceed roughly 425&ndash;450&nbsp;°F.
      </p>
      <p>
        Set that against the polymer. <strong>PET melts at
        255&ndash;265&nbsp;°C, and thermal degradation begins near the melting
        point.</strong> A bag operating at 232&nbsp;°C is running roughly
        25&ndash;30&nbsp;°C below the temperature at which the film it is made
        from starts to come apart — and the fact that manufacturers engineer an
        explicit ceiling into the design tells you they know exactly where that
        line is.
      </p>

      <BlogComparisonTable
        caption="What is actually in a microwave popcorn bag (checked August 2026)"
        columns={[
          { key: 'l', label: 'Layer' },
          { key: 'w', label: 'What it is' },
          { key: 's', label: 'Status' },
        ]}
        rows={[
          { l: 'Outer paper', w: 'Paperboard, printed', s: 'Not the issue' },
          { l: 'Grease-proofing treatment', w: 'Historically PFAS-based coatings', s: 'Reported off the US market since Feb 2024, voluntarily' },
          { l: 'Susceptor film', w: 'Aluminium vapour-deposited onto PET polyester', s: 'Still present — it is what makes the product work' },
          { l: 'Adhesive laminate', w: 'Bonds film to paper', s: 'Rarely disclosed by any manufacturer' },
          { l: 'Oil and flavouring', w: 'Fat in direct contact with all of the above at 232 °C', s: 'Fat is the carrier that mobilises what migrates' },
        ]}
      />

      <BlogAppCTA
        headline="Not sure what the packaging in your cupboard is made of?"
        sub="Scan a wrapper, a bag or a box and get the polymer, a 0–100 risk score, and a lower-plastic swap in about five seconds."
      />

      <h2>What we still do not know</h2>
      <ul>
        <li>
          <strong>No one has counted microplastic particles from a popcorn
          bag.</strong> There is no published measurement of particle release
          from a susceptor film under real popping conditions. If you see a
          figure, it was invented. This is a mechanism worth studying, not a
          quantified exposure.
        </li>
        <li>
          <strong>No post-2024 serum study exists.</strong> Susmann tells us the
          old coatings reached blood. Nothing tells us what current US bags do.
        </li>
        <li>
          <strong>What replaced the PFAS is not disclosed.</strong> Bags still
          need to resist grease. The substitutes are not named on any package,
          and &ldquo;PFAS-free&rdquo; describes what a coating is not, rather
          than what it is.
        </li>
        <li>
          <strong>The laminate adhesive is undocumented.</strong> No consumer
          brand publishes it.
        </li>
      </ul>

      <h2>What to actually do</h2>
      <p>
        This is one of the rare cases where the swap is complete rather than
        incremental, and costs less than the thing it replaces:
      </p>
      <ul>
        <li>
          <strong>Loose kernels in a pot.</strong> Two tablespoons of oil, a lid,
          medium heat. No bag, no susceptor, no coating, and plain kernels are a
          fraction of the price per serving of the bagged version.
        </li>
        <li>
          <strong>An air popper</strong> if you want it hands-off. Check the
          chute material — many are plastic, but the kernels pass through in
          seconds rather than sitting against a 232&nbsp;°C film.
        </li>
        <li>
          <strong>A glass bowl with a lid or plate in the microwave.</strong>{' '}
          Kernels, a plate on top, three minutes. It genuinely works and keeps
          the convenience.
        </li>
        <li>
          <strong>Not a silicone popper on the strength of the material
          alone.</strong> Silicone is more heat-stable than PET, but it is{' '}
          <Link href="/blog/is-silicone-a-microplastic" className="text-forest underline">far less studied</Link>,
          and a bowl you already own solves the same problem.
        </li>
        <li>
          <strong>If you are keeping the bags, do not re-microwave one.</strong>{' '}
          The susceptor is designed for a single controlled heating cycle, and
          an empty or re-run bag is the condition that pushes the film past its
          intended ceiling.
        </li>
      </ul>

      <p>
        Related reading:{' '}
        <Link href="/blog/what-does-microwave-safe-actually-mean" className="text-forest underline">what &ldquo;microwave safe&rdquo; actually means</Link>,{' '}
        <Link href="/blog/pfas-forever-chemicals-food-water-explained" className="text-forest underline">PFAS explained</Link>,{' '}
        <Link href="/blog/microplastics-vs-pfas-vs-bpa" className="text-forest underline">microplastics vs PFAS vs BPA</Link>,{' '}
        <Link href="/blog/can-you-microwave-ziploc-bags" className="text-forest underline">can you microwave Ziploc bags</Link>, and{' '}
        <Link href="/blog/microplastics-in-fast-food" className="text-forest underline">microplastics in fast food</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do microwave popcorn bags still have PFAS?',
            answer: 'For bags manufactured for the US market, largely no. On 28 February 2024 the FDA announced that grease-proofing substances containing PFAS are no longer sold by manufacturers for food-contact use in the US, naming microwave popcorn bags directly. Two caveats matter: it was a voluntary phase-out of sales rather than a ban, with no recall and no verification testing attached; and it applies only to the US market. A 2025 study of five bags bought in Taiwan still found seven PFAS migrating out of them.',
          },
          {
            question: 'Is the 63% higher PFAS statistic still accurate?',
            answer: 'It is accurate about the period it measured, which is not now. Susmann et al. (Environmental Health Perspectives, 2019) analysed NHANES serum and dietary data from 2003 to 2014 and found people eating microwave popcorn daily carried PFAS levels up to 63% above average. Every sample predates the phase-out of the coatings. It is strong evidence that those coatings reached people, not evidence about a bag bought today. No equivalent study has been run since.',
          },
          {
            question: 'What is the susceptor in a microwave popcorn bag?',
            answer: 'A strip of PET polyester film with an extremely thin layer of aluminium vapour-deposited onto it, laminated inside the paper bag. Microwaves pass through paper and food but are absorbed by the metal layer, which converts the energy into direct heat so the kernels reach popping temperature. Every microwave popcorn bag contains one, as do the crisping sleeves on frozen sandwiches and pizzas. It sits in direct contact with the food and the oil.',
          },
          {
            question: 'How hot does a microwave popcorn bag actually get?',
            answer: 'Pop Secret states in its own FAQ that the bag reaches around 450°F, which is 232°C. Patent literature for susceptor packaging describes films reaching up to 500°F (about 260°C), with designs engineered so the polyester next to the susceptor stays below roughly 425–450°F. For context, PET melts at 255–265°C and begins thermally degrading near its melting point, so the film runs about 25–30°C under that line by design.',
          },
          {
            question: 'Do microwave popcorn bags release microplastics?',
            answer: 'Nobody has measured it. There is no published study counting particle release from a susceptor film under real popping conditions, so any specific number you encounter is invented. What is documented is the mechanism: a PET film held at roughly 232°C in direct contact with hot fat, which is the combination of heat and a fatty carrier that drives migration from plastics generally. That is a reason to prefer loose kernels, not a measured exposure.',
            },
          {
            question: 'Is stovetop or air-popped popcorn actually better?',
            answer: 'Yes, and it is cheaper. Loose kernels in a pot with a little oil, or a glass bowl with a plate over it in the microwave, removes the bag, the susceptor film and whatever grease-proofing coating replaced the PFAS, all at once. Plain kernels also cost a fraction of the bagged version per serving. Air poppers work too, though many have plastic chutes — the difference is that kernels pass through in seconds rather than resting against a 232°C film.',
          },
          {
            question: 'Is "PFAS-free" on a popcorn box meaningful?',
            answer: 'Partly. It tells you what the coating is not, and given the 2024 phase-out it is now close to the industry default for US bags rather than a distinguishing feature. It tells you nothing about what replaced it, since substitute grease-proofing chemistries are not disclosed on packaging, and nothing at all about the susceptor film, which is unaffected by any PFAS claim and is present in every bag regardless.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'US Food and Drug Administration', year: 2024, title: 'Market phase-out of grease-proofing substances containing PFAS — announced 28 February 2024; a voluntary phase-out of sales for food-contact use following 2020 manufacturer commitments, naming microwave popcorn bags, fast-food wrappers, takeaway paperboard and pet food bags', publisher: 'US FDA', url: 'https://www.fda.gov/food/process-contaminants-food/market-phase-out-grease-proofing-substances-containing-pfas' },
          { authors: 'Susmann HP, Schaider LA, Rodgers KM, Rudel RA', year: 2019, title: 'Dietary habits related to food packaging and population exposure to PFASs — NHANES 2003–2014; microwave popcorn consumption associated with serum PFAS up to 63% above average in daily consumers', publisher: 'Environmental Health Perspectives 127(10)', url: 'https://ehp.niehs.nih.gov/doi/10.1289/EHP4092' },
          { authors: 'Hsu JY, Jiang HJ, Chang CW, Chen YC, Liao PC', year: 2025, title: 'High-resolution mass spectrometry for identification, quantification and risk assessment of 40 PFAS migrating from microwave popcorn bags — five bags purchased in Taiwan; seven PFAS detected migrating at 5.2–274.0 ng/g; EFSA hazard ratios 0.01–0.8 versus EPA hazard ratios 0.3–142.3 on the same samples', publisher: 'Molecules', url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12073814/' },
          { authors: 'Pop Secret', year: 2026, title: 'Frequently asked questions — "when the Pop Secret bag is heated in the microwave and the temperature reaches around 450°F, the moisture in the kernel turns to steam and the kernel explodes or pops"', publisher: 'Pop Secret (manufacturer)', url: 'https://www.popsecret.com/faq' },
        ]}
      />
    </BlogPostLayout>
  )
}
